import { useDeleteImporter } from '@/features/importers/hooks/importers'
import useI18n from '@/shared/hooks/usei18n'
import { Alert, Button, GenericDialog } from '@/common'
import { useCallback, useState } from 'react'
import toast from 'react-hot-toast'
import { Trans } from 'react-i18next'

export default function DeleteImporterDialog({
  isOpen,
  close,
  importerName,
  organizationId,
  importerId,
  onAfterLeave
}: {
  isOpen: boolean
  close: () => void
  importerName: string
  organizationId: string
  importerId: string
  onAfterLeave?: () => void
}) {
  const { t } = useI18n()
  const { mutateAsync: deleteImporter, isPending: isDeleting } = useDeleteImporter(
    organizationId,
    importerId
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const onDeleteClick = useCallback(async () => {
    try {
      setErrorMessage(null)
      await deleteImporter()
      toast.success(t('importers:importersPage.dialogs.deleteImporter.notifications.success'))
      close()
      if (onAfterLeave) {
        onAfterLeave()
      }
    } catch (error: any) {
      const errorCode = error?.response?.data?.message

      if (errorCode === 'IMPORTER_HAS_IMPORTED_GOODS') {
        setErrorMessage(
          t('importers:importersPage.dialogs.deleteImporter.errors.importerHasImportedGoods')
        )
      } else {
        toast.error(t('importers:importersPage.dialogs.deleteImporter.notifications.error'))
      }
    }
  }, [deleteImporter, close, t, onAfterLeave])

  const onClose = useCallback(() => {
    close()
    setErrorMessage(null)
  }, [close])

  return (
    <GenericDialog
      isOpen={isOpen}
      close={onClose}
      title={t('importers:importersPage.dialogs.deleteImporter.title')}
      size="md"
    >
      <div className="space-y-4">
        {errorMessage && <Alert type="error">{errorMessage}</Alert>}
        <p>
          <Trans
            i18nKey="importers:importersPage.dialogs.deleteImporter.description"
            values={{ name: importerName }}
            components={{ strong: <strong /> }}
          />
        </p>
        <footer className="gap-2 flex justify-end">
          <Button variant="neutral" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="danger" onClick={onDeleteClick} isLoading={isDeleting}>
            {t('common:actions.delete')}
          </Button>
        </footer>
      </div>
    </GenericDialog>
  )
}
